import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ComboboxItem, ComboboxList } from '@/components/ui/combobox'

const DEFAULT_ROW_HEIGHT_PX = 32
const DEFAULT_OVERSCAN = 8

type VirtualizedComboboxListProps<T> = {
  items: T[]
  open: boolean
  estimateSize?: number
  overscan?: number
  getItemKey: (item: T, index: number) => string
  renderItem: (item: T, index: number) => ReactNode
  getItemValue: (item: T, index: number) => T
}

export function VirtualizedComboboxList<T>({
  items,
  open,
  estimateSize = DEFAULT_ROW_HEIGHT_PX,
  overscan = DEFAULT_OVERSCAN,
  getItemKey,
  renderItem,
  getItemValue,
}: VirtualizedComboboxListProps<T>) {
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollElement,
    estimateSize: () => estimateSize,
    overscan,
  })

  const virtualRows = virtualizer.getVirtualItems()

  useEffect(() => {
    if (!open || scrollElement == null) {
      return
    }
    virtualizer.measure()
  }, [open, scrollElement, virtualizer])

  return (
    <ComboboxList ref={setScrollElement}>
      <div
        className='relative w-full'
        style={{ height: `${virtualizer.getTotalSize()}px` }}
      >
        {virtualRows.map((virtualRow) => {
          const item = items[virtualRow.index]
          if (item === undefined) {
            return null
          }
          return (
            <div
              key={getItemKey(item, virtualRow.index)}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              className='absolute top-0 left-0 w-full'
              style={{ transform: `translateY(${virtualRow.start}px)` }}
            >
              <ComboboxItem value={getItemValue(item, virtualRow.index)} index={virtualRow.index}>
                {renderItem(item, virtualRow.index)}
              </ComboboxItem>
            </div>
          )
        })}
      </div>
    </ComboboxList>
  )
}
